import { ensureNoUndefinedProperties } from '@/Shared/Helper/EnsureNoUndefinedProperties'
import {
    type CustomFieldResponseDTO,
    type DraftMeetingData,
} from '@/Shared/AutoGenerated/Main'
import ServicePriceID from '@/App/Service/Entity/ServicePriceID'
import CustomFieldID from '@/App/CustomField/Entity/CustomFieldID'
import DraftMeetingID from '@/App/Booking/Draft/Entity/DraftMeetingID'

export default class DraftMeeting {
    constructor(
        public readonly id: DraftMeetingID,
        public readonly servicePriceId: ServicePriceID | null,
        public readonly selectedDateInSelectedTimeZoneForDisplay: string | null,
        public readonly selectedTimeSlotStartInSelectedTimeZoneForDisplay:
            string | null,
        public readonly selectedTimeSlotEndInSelectedTimeZoneForDisplay:
            string | null,
        public readonly selectedDateInAvailabilityTimeZoneForScheduling:
            string | null,
        public readonly selectedTimeSlotStartInAvailabilityTimeZoneForScheduling:
            string | null,
        public readonly selectedTimeSlotEndInAvailabilityTimeZoneForScheduling:
            string | null,
        public readonly selectedTimeZone: string | null,
        public readonly selectedMeetingLocationId: string | null,
        public readonly phoneNumber: string | null,
        public readonly phoneCountryCode: string | null,
        public readonly inPersonAttendeeDefinedMeetingLocationAddress:
            string | null,
        public readonly notes: string | null,
        public readonly customerName: string | null,
        public readonly customerEmail: string | null,
        public readonly customFieldResponsesData:
            CustomFieldResponseDTO[] | null,
        public readonly numberOfRecurringMeetingsToSchedule: number | null,
        public readonly numberOfRequestedSeats: number | null,
        public readonly savePaymentMethod: boolean | null,
        // BE Computed
        public readonly selectedDateInSelectedTimeZoneForDisplayFormatted:
            string | null,
        public readonly selectedTimeSlotStartInSelectedTimeZoneForDisplayFormatted:
            string | null,
        public readonly selectedTimeSlotEndInSelectedTimeZoneForDisplayFormatted:
            string | null,
        public readonly selectedDateInAvailabilityTimeZoneForSchedulingFormatted:
            string | null,
        public readonly selectedTimeSlotStartInAvailabilityTimeZoneForSchedulingFormatted:
            string | null,
        public readonly selectedTimeSlotEndInAvailabilityTimeZoneForSchedulingFormatted:
            string | null,
    ) {
        ensureNoUndefinedProperties(this)
    }

    getSavedResponseForCustomField(
        customFieldId: CustomFieldID,
    ): CustomFieldResponseDTO {
        const nullCustomFieldResponseDto: CustomFieldResponseDTO = {
            customFieldId: customFieldId.value,
            response: null,
        }

        if (!this.customFieldResponsesData) {
            return nullCustomFieldResponseDto
        }

        const customFieldResponseDto = this.customFieldResponsesData?.find(
            (dto) => {
                return dto.customFieldId === customFieldId.value
            },
        )

        return customFieldResponseDto ?? nullCustomFieldResponseDto
    }

    // ------------------------
    // Hydration
    // ------------------------

    static hydrateSingle(entityData: DraftMeetingData): DraftMeeting {
        const servicePriceId =
            typeof entityData.servicePriceId === 'number'
                ? new ServicePriceID(entityData.servicePriceId)
                : null

        return new DraftMeeting(
            new DraftMeetingID(entityData.id),
            servicePriceId,
            entityData.selectedDateInSelectedTimeZoneForDisplay,
            entityData.selectedTimeSlotStartInSelectedTimeZoneForDisplay,
            entityData.selectedTimeSlotEndInSelectedTimeZoneForDisplay,
            entityData.selectedDateInAvailabilityTimeZoneForScheduling,
            entityData.selectedTimeSlotStartInAvailabilityTimeZoneForScheduling,
            entityData.selectedTimeSlotEndInAvailabilityTimeZoneForScheduling,
            entityData.selectedTimeZone,
            entityData.selectedMeetingLocationId,
            entityData.phoneNumber,
            entityData.phoneCountryCode,
            entityData.inPersonAttendeeDefinedMeetingLocationAddress,
            entityData.notes,
            entityData.customerName,
            entityData.customerEmail,
            entityData.customFieldResponsesData,
            entityData.numberOfRecurringMeetingsToSchedule,
            entityData.numberOfRequestedSeats,
            entityData.savePaymentMethod,
            // Computed
            entityData.selectedDateInSelectedTimeZoneForDisplayFormatted,
            entityData.selectedTimeSlotStartInSelectedTimeZoneForDisplayFormatted,
            entityData.selectedTimeSlotEndInSelectedTimeZoneForDisplayFormatted,
            entityData.selectedDateInAvailabilityTimeZoneForSchedulingFormatted,
            entityData.selectedTimeSlotStartInAvailabilityTimeZoneForSchedulingFormatted,
            entityData.selectedTimeSlotEndInAvailabilityTimeZoneForSchedulingFormatted,
        )
    }
}
